/**
 * The preset vocabulary for `r-button`.
 *
 * Three independent axes rather than one flat list of named buttons: five
 * variants times four tones times five sizes is a hundred looks from three
 * short tables, and an author picks each axis without us having to have
 * anticipated the combination.
 *
 * Every preset name here is persisted customer data, exactly like an icon
 * name. A name that has shipped is never deleted or repurposed — add a new
 * one and alias the old one, or published pages change appearance on upgrade.
 */

/** How the button is filled and bordered. */
export const BUTTON_VARIANTS = ['solid', 'soft', 'outline', 'ghost', 'link'] as const;

/** Which colour family the variant draws from. */
export const BUTTON_TONES = ['brand', 'neutral', 'danger', 'success'] as const;

/** Type scale, padding and control height, smallest to largest. */
export const BUTTON_SIZES = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];
export type ButtonTone = (typeof BUTTON_TONES)[number];
export type ButtonSize = (typeof BUTTON_SIZES)[number];

export type ButtonPreset = {
  variant: ButtonVariant;
  tone: ButtonTone;
  size: ButtonSize;
};

/**
 * What an unconfigured button looks like. Dropping the component on a canvas
 * and touching nothing must already produce something shippable.
 */
export const DEFAULT_BUTTON_PRESET: ButtonPreset = {
  variant: 'solid',
  tone: 'brand',
  size: 'md',
};

export const isButtonVariant = (value: unknown): value is ButtonVariant =>
  BUTTON_VARIANTS.includes(value as ButtonVariant);

export const isButtonTone = (value: unknown): value is ButtonTone =>
  BUTTON_TONES.includes(value as ButtonTone);

export const isButtonSize = (value: unknown): value is ButtonSize =>
  BUTTON_SIZES.includes(value as ButtonSize);

/**
 * The colour slots a tone fills.
 *
 * `hover` / `active` / `softHover` are consumed by the library stylesheet
 * (`button-library-stylesheet.ts`) via the per-instance fill slots. Changing
 * a tone here is still a palette change, not a delivery change.
 */
export type TonePalette = {
  /** Solid fill, and the text colour for outline / ghost / link. */
  base: string;
  hover: string;
  active: string;
  /** Text placed on top of `base`. */
  contrast: string;
  /** Outline border, kept separate so neutral can be lighter than its text. */
  border: string;
  /** Tinted fill for the soft variant. */
  soft: string;
  softHover: string;
  /** Text placed on top of `soft`. */
  softContrast: string;
};

/**
 * Defaults only. Every one of these reaches the page as the fallback half of a
 * `var(--rulecms-button-…, default)`, so a customer rebrands every button on
 * their site by defining the custom property once, with no per-button edits
 * and no composer access. See `button-css-variables.ts`.
 */
export const TONE_PALETTES: Record<ButtonTone, TonePalette> = {
  brand: {
    base: '#2563eb',
    hover: '#1d4ed8',
    active: '#1e40af',
    contrast: '#ffffff',
    border: '#2563eb',
    soft: '#eff6ff',
    softHover: '#dbeafe',
    softContrast: '#1d4ed8',
  },
  neutral: {
    base: '#334155',
    hover: '#1e293b',
    active: '#0f172a',
    contrast: '#ffffff',
    // Deliberately lighter than `base`: a secondary button whose border matches
    // its dark label text reads as heavy as a primary one.
    border: '#cbd5e1',
    soft: '#f1f5f9',
    softHover: '#e2e8f0',
    softContrast: '#334155',
  },
  danger: {
    base: '#dc2626',
    hover: '#b91c1c',
    active: '#991b1b',
    contrast: '#ffffff',
    border: '#dc2626',
    soft: '#fef2f2',
    softHover: '#fee2e2',
    softContrast: '#b91c1c',
  },
  success: {
    base: '#16a34a',
    hover: '#15803d',
    active: '#166534',
    contrast: '#ffffff',
    border: '#16a34a',
    soft: '#f0fdf4',
    softHover: '#dcfce7',
    softContrast: '#15803d',
  },
};

export type SizeScale = {
  fontSize: string;
  /** Floor for the control height. WCAG 2.2 AA wants at least 24px. */
  minHeight: string;
  paddingInline: string;
  paddingBlock: string;
  borderRadius: string;
  /** Space between an icon and the label. */
  gap: string;
};

export const SIZE_SCALES: Record<ButtonSize, SizeScale> = {
  xs: {
    fontSize: '12px',
    minHeight: '28px',
    paddingInline: '10px',
    paddingBlock: '4px',
    borderRadius: '4px',
    gap: '4px',
  },
  sm: {
    fontSize: '14px',
    minHeight: '34px',
    paddingInline: '12px',
    paddingBlock: '6px',
    borderRadius: '6px',
    gap: '6px',
  },
  md: {
    fontSize: '16px',
    minHeight: '40px',
    paddingInline: '16px',
    paddingBlock: '8px',
    borderRadius: '8px',
    gap: '8px',
  },
  lg: {
    fontSize: '18px',
    minHeight: '48px',
    paddingInline: '20px',
    paddingBlock: '10px',
    borderRadius: '10px',
    gap: '8px',
  },
  xl: {
    fontSize: '20px',
    minHeight: '56px',
    paddingInline: '24px',
    paddingBlock: '12px',
    borderRadius: '12px',
    gap: '10px',
  },
};
